import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, UntypedFormControl } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { faCaretDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { FormFocusDirective } from 'src/app/core/directives/form-focus.directive';
import { View } from 'src/app/models/server/view';
import { ViewsStateService } from 'src/app/services/state/views-state.service';

@Component({
    selector: 'app-view-selector',
    templateUrl: './view-selector.component.html',
    styleUrls: ['./view-selector.component.scss'],
    imports: [FormsModule, FormFocusDirective, MatFormFieldModule, MatSelectModule, MatOptionModule]
})
export class ViewSelectorComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    public faFilter = faFilter;
    public faCaretDown = faCaretDown;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Obtient la vue selectionnée par défaut.
     */
    @Input()
    set defaultSelectedView(value: View) {
        if (value && (this.selectedView === null || value.id !== this.selectedView.id)) {
            this.selectedView = value;
            this.changeCurrentView(value);
        }
    }

    /**
     * Emis lorsque l'utilisateur selectionne une vue.
     */
    @Output()
    public viewSelected: EventEmitter<View> = new EventEmitter();

    /**
     * vue selectionnée par l'utilisateur.
     */
    public selectedView: View = null;

    /**
     * Contrôle utilisé pour la saisie de l et l'affichage de l'organisation selectionnée.
     */
    selectFormControl: UntypedFormControl;

    /**
     * Constructeur.
     */
    constructor(public viewsStateService: ViewsStateService) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit() {
        // Initialise le Contrôle utilisé pour la saisie de l'organisation avec l'organisation par défaut.
        this.selectFormControl = new UntypedFormControl(this.defaultSelectedView);

        this.subscribeToViewsStateEvent();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne une vue.
     */
    public viewChange(event: MatSelectChange): void {
        this.selectedView = this.viewsStateService.boxViews.find((x) => x.id === event.value);

        if (!this.selectedView) {
            this.selectedView = this.viewsStateService.workflowViews.find((x) => x.id === event.value);
        }

        if (!this.selectedView) {
            this.selectedView = this.viewsStateService.archivingViews.find((x) => x.id === event.value);
        }

        this.changeCurrentView(this.selectedView);
    }

    /**
     * Change la vue courante.
     * @param view : la vue.
     */
    private changeCurrentView(view: View) {
        this.viewSelected.emit(view);
    }

    /**
     * S'abonne aux évènements de changement de vue.
     */
    private subscribeToViewsStateEvent() {
        this.viewsStateService.onViewsSelectionChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (x?.currentView && x?.currentView.id !== this.selectedView.id) {
                // S'abonne aux évènements de changement de vue extérieur afin de mettre à jour l'element selectionné dans la liste déroulante.
                this.selectedView = x.currentView;
            }
        });
    }
}
